import { Injectable, NotFoundException, ConflictException } from '@nestjs/common';
import { InjectModel } from '@nestjs/mongoose';
import { Model } from 'mongoose';
import { Country, CountryDocument } from '../schemas/country.schema';
import { State, StateDocument } from '../schemas/state.schema';
import { CreateCountryDto } from '../dto/create-country.dto';   
import { UpdateCountryDto } from '../dto/update-country.dto';

@Injectable()
export class CountryService {
  constructor(
    @InjectModel(Country.name) private countryModel: Model<CountryDocument>,
    @InjectModel(State.name) private stateModel: Model<StateDocument>,
  ) {}

  async create(createCountryDto: CreateCountryDto): Promise<Country> {
    // Check if country with same code already exists
    const existingCountry = await this.countryModel.findOne({ 
      code: createCountryDto.code 
    });
    
    if (existingCountry) {
      throw new ConflictException(`Country with code ${createCountryDto.code} already exists`);
    }

    const createdCountry = new this.countryModel(createCountryDto);
    return createdCountry.save();
  }

  async findAll(): Promise<Country[]> {
    return this.countryModel.find({ isActive: true }).sort({ name: 1 }).exec();
  }

  async findAllWithStates(): Promise<Country[]> {
    return this.countryModel.aggregate([
      { $match: { isActive: true } },
      {
        $lookup: {
          from: 'states',
          localField: '_id',
          foreignField: 'countryId',
          as: 'states',
          pipeline: [
            { $match: { isActive: true } },
            { $sort: { name: 1 } }
          ]
        }
      },
      { $sort: { name: 1 } }
    ]);
  }

  async findOne(id: string): Promise<Country> {
    const country = await this.countryModel.findById(id).exec();
    if (!country) {
      throw new NotFoundException(`Country with ID ${id} not found`);
    }
    return country;
  }

  async findOneWithStates(id: string): Promise<Country> {
    const country = await this.countryModel.aggregate([
      { $match: { _id: id, isActive: true } },
      {
        $lookup: {
          from: 'states',
          localField: '_id',
          foreignField: 'countryId',
          as: 'states',
          pipeline: [
            { $match: { isActive: true } },
            { $sort: { name: 1 } }
          ]
        }
      }
    ]);

    if (!country || country.length === 0) {
      throw new NotFoundException(`Country with ID ${id} not found`);
    }

    return country[0];
  }

  async update(id: string, updateCountryDto: UpdateCountryDto): Promise<Country> {
    // Check if country with same code already exists (excluding current country)
    if (updateCountryDto.code) {
      const existingCountry = await this.countryModel.findOne({ 
        code: updateCountryDto.code,
        _id: { $ne: id }
      });
      
      if (existingCountry) {
        throw new ConflictException(`Country with code ${updateCountryDto.code} already exists`);
      }
    }

    const updatedCountry = await this.countryModel
      .findByIdAndUpdate(id, updateCountryDto, { new: true })
      .exec();
    
    if (!updatedCountry) {
      throw new NotFoundException(`Country with ID ${id} not found`);
    }
    
    return updatedCountry;
  }

  async remove(id: string): Promise<void> {
    // Check if country has states
    const statesCount = await this.stateModel.countDocuments({ countryId: id });
    if (statesCount > 0) {
      throw new ConflictException('Cannot delete country that has states. Delete states first.');
    }

    const result = await this.countryModel.findByIdAndDelete(id).exec();
    if (!result) {
      throw new NotFoundException(`Country with ID ${id} not found`);
    }
  }

  async softDelete(id: string): Promise<Country> {
    const updatedCountry = await this.countryModel
      .findByIdAndUpdate(id, { isActive: false }, { new: true })
      .exec();
    
    if (!updatedCountry) {
      throw new NotFoundException(`Country with ID ${id} not found`);
    }

    // Also soft delete all states of this country
    await this.stateModel.updateMany(
      { countryId: id },
      { isActive: false }
    ).exec();
    
    return updatedCountry;
  }

  async getShippingCost(countryId: string, stateId?: string): Promise<number> {
    if (stateId) {
      const state = await this.stateModel.findById(stateId).exec();
      if (!state || state.countryId.toString() !== countryId) {
        throw new NotFoundException('State not found or does not belong to the specified country');
      }
      return state.shippingCost;
    } else {
      const country = await this.countryModel.findById(countryId).exec();
      if (!country) {
        throw new NotFoundException('Country not found');
      }
      return country.defaultShippingCost;
    }
  }
}